import React, { useMemo, useState } from 'react';
import { Box, Chip, Divider, IconButton, Stack, Tooltip, Typography, useTheme, Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Line, LineChart, ResponsiveContainer, Tooltip as RTooltip, YAxis } from 'recharts';

type Metric = {
  label: string;
  value: number | string;
  unit?: string;
  deltaPct?: number; // percent change
  positiveIsGood?: boolean; // default true
};

export interface RunFinalViewProps {
  view?: 'marketWrap' | 'metrics' | 'list' | 'auto';
  data?: any;
  text?: string;
}

const formatNumber = (n: number | string | undefined, unit?: string) => {
  if (n === undefined || n === null) return '';
  if (typeof n === 'string') return n;
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  const abs = Math.abs(num);
  const formatted = abs >= 100 ? num.toFixed(2) : abs >= 10 ? num.toFixed(2) : num.toFixed(3);
  return unit ? `${formatted}${unit}` : formatted;
};

const DeltaChip: React.FC<{ pct?: number; positiveIsGood?: boolean }> = ({ pct, positiveIsGood = true }) => {
  const theme = useTheme();
  if (pct === undefined || pct === null || Number.isNaN(Number(pct))) return null as any;
  const val = Number(pct);
  const isUp = val >= 0;
  const isGood = positiveIsGood ? isUp : !isUp;
  const color = isUp ? (isGood ? theme.palette.success.main : theme.palette.warning.main) : (isGood ? theme.palette.success.main : theme.palette.error.main);
  const sign = val > 0 ? '+' : '';
  return (
    <Chip size="small" label={`${sign}${val.toFixed(2)}%`} sx={{ bgcolor: color, color: '#000', height: 20 }} />
  );
};

const Sparkline: React.FC<{ series?: Array<number | { value: number }>; color?: string }> = ({ series, color }) => {
  const data = useMemo(() => {
    if (!Array.isArray(series) || series.length === 0) return [];
    return series.map((d, i) => ({ i, v: typeof d === 'number' ? d : d.value }));
  }, [series]);
  if (data.length === 0) return null as any;
  return (
    <Box sx={{ width: '100%', height: 52 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 6, right: 6, left: 6, bottom: 0 }}>
          <YAxis hide domain={['auto', 'auto']} />
          <RTooltip formatter={(v: any) => v.toFixed ? v.toFixed(2) : v} labelFormatter={() => ''} />
          <Line type="monotone" dataKey="v" stroke={color || '#65D38B'} strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const RunFinalView: React.FC<RunFinalViewProps> = ({ view = 'auto', data, text }) => {
  const theme = useTheme();
  const [showFull, setShowFull] = useState(false);

  const metrics: Metric[] | undefined = data?.metrics || data?.priceMoves?.metrics;
  const highlights: string[] | undefined = data?.highlights || data?.bullets;
  const series = data?.sparkline || data?.series;
  const title = data?.header?.title || data?.title;
  const subtitle = data?.header?.subtitle || data?.subtitle;
  const symbol = data?.symbol || data?.ticker;

  const resolvedView: 'marketWrap' | 'metrics' | 'list' | 'text' = (() => {
    if (view !== 'auto') return view as any;
    if (data?.priceMoves || (Array.isArray(metrics) && metrics.length > 0)) return 'marketWrap';
    if (Array.isArray(highlights) && highlights.length > 0) return 'list';
    if (Array.isArray(metrics) && metrics.length > 0) return 'metrics';
    return 'text';
  })();

  const Header = (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      {symbol && (
        <Chip size="small" color="primary" label={symbol} />
      )}
      {title && (
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
      )}
      {subtitle && (
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      )}
    </Stack>
  );

  const renderMetricsRow = () => (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 0.5 }}>
      {(metrics || []).slice(0, 6).map((m: Metric, i: number) => (
        <Stack key={i} direction="row" spacing={0.75} alignItems="center" sx={{
          p: 0.75,
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}>
          <Typography variant="caption" color="text.secondary">{m.label}</Typography>
          <Typography variant="body2" fontWeight={600}>{formatNumber(m.value, m.unit)}</Typography>
          {m.deltaPct !== undefined && (
            <DeltaChip pct={m.deltaPct} positiveIsGood={m.positiveIsGood !== false} />
          )}
        </Stack>
      ))}
    </Stack>
  );

  const ReadMore = (
    text ? (
      <Box sx={{ mt: 1 }}>
        {!showFull ? (
          <Button size="small" onClick={() => setShowFull(true)}>Read full</Button>
        ) : (
          <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Full write‑up</Typography>
              <Tooltip title="Copy full text">
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(text)}>
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.default', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              <Typography variant="body2">{text}</Typography>
            </Box>
          </>
        )}
      </Box>
    ) : null
  );

  if (resolvedView === 'text') {
    return (
      <>
        {Header}
        {text && (
          <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.default', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            <Typography variant="body2">{text}</Typography>
          </Box>
        )}
      </>
    );
  }

  if (resolvedView === 'list') {
    return (
      <>
        {Header}
        <Stack spacing={0.5}>
          {(highlights || []).slice(0, 6).map((h, i) => (
            <Typography key={i} variant="body2">• {h}</Typography>
          ))}
        </Stack>
        {ReadMore}
      </>
    );
  }

  if (resolvedView === 'metrics' || resolvedView === 'marketWrap') {
    return (
      <>
        {Header}
        {renderMetricsRow()}
        {Array.isArray(series) && series.length > 3 && (
          <Sparkline series={series} />
        )}
        {Array.isArray(highlights) && highlights.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={0.5}>
              {highlights.slice(0, 5).map((h: string, i: number) => (
                <Typography key={i} variant="body2">• {h}</Typography>
              ))}
            </Stack>
          </>
        )}
        {ReadMore}
      </>
    );
  }

  // Fallback
  return (
    <>
      {Header}
      {text && (
        <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.default' }}>
          <Typography variant="body2">{text}</Typography>
        </Box>
      )}
    </>
  );
};

export default RunFinalView;

